import { Link2 } from "lucide-react";
import { Link, Outlet, useLocation, useParams } from "react-router-dom";
import type { VinculoDoBeneficiario } from "@/api/contrato";
import { useVinculosDoBeneficiario } from "@/api/consultas";
import { Botao } from "@/componentes/Botao";
import { Esqueleto } from "@/componentes/Esqueleto";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { useModalNaUrl } from "@/componentes/useModalNaUrl";
import { dia } from "@/formato";
import { rotaDoVinculo } from "@/rotas";
import { useSessao } from "@/sessao/useSessao";
import { consumoDoPar } from "@/telas/sistemas/cedente";
import { SituacaoDoPar } from "@/telas/sistemas/SituacaoDoPar";
import { MotivoDoPar } from "./DetalheDoVinculo";
import { motivoJaDito, pendenciaOferece } from "./pendencias";
import { useCedenteDaRota } from "./useCedenteDaRota";
import { VincularAplicacao } from "./VincularAplicacao";

/*
 * `/cedentes/:id/aplicacoes`: é a pergunta que a clínica faz ao suporte, "por que não
 * emito pelo Agenda Maker?", lida do lado dela. Em `por_cedente` o contrato é dela, e
 * aparece aqui. Em `por_sistema` quem paga é a aplicação, e a linha diz só isso.
 *
 * A linha é link real para o painel do vínculo, e o `Outlet` é esse painel: rota filha,
 * com a aba montada embaixo, na mesma rolagem (design D1).
 *
 * Com `Sem aplicação` na lista de pendências, a aba não repete o vazio nem o botão.
 */
function contratoDoVinculo(vinculo: VinculoDoBeneficiario): string {
  if (vinculo.modalidade_cobranca === "por_sistema") return "contrato da aplicação";
  if (vinculo.contrato === null) return "sem contrato";

  const { plano_nome, inicio, fim } = vinculo.contrato;
  return `${plano_nome} · ${dia(inicio)} → ${fim === null ? "aberto" : dia(fim)}`;
}

export function AbaDeAplicacoes() {
  const { id, pessoa, prontidao, consulta } = useCedenteDaRota();
  const { sistemaId } = useParams();
  const { cliente, pode } = useSessao();
  const modal = useModalNaUrl();
  // O "« voltar" da página continua sabendo de que lista o operador veio.
  const estado: unknown = useLocation().state;

  const { data, error, isPending } = useVinculosDoBeneficiario(cliente, id);
  const vinculos = data?.vinculos ?? [];
  const ehAdmin = pode("admin");
  const naLista = pendenciaOferece(prontidao, "Vincular aplicação");

  return (
    <div aria-busy={isPending} className="flex flex-col gap-3">
      {ehAdmin && !naLista && (
        <div className="flex justify-end">
          <Botao
            disabled={data === undefined || consulta.isPending}
            onClick={() => modal.abrirModal({ abrir: "vincular" })}
          >
            <Link2 aria-hidden="true" className="size-4" />
            Vincular aplicação
          </Botao>
        </div>
      )}

      {error ? (
        <FaixaDeErro erro={error} />
      ) : isPending || consulta.isPending ? (
        <Esqueleto className="h-14" />
      ) : vinculos.length === 0 ? (
        !naLista && <p className="text-texto-2">Nenhuma aplicação.</p>
      ) : (
        <ul aria-label="Aplicações" className="flex flex-col gap-1">
          {vinculos.map((vinculo) => {
            const aberta = vinculo.sistema_id === sistemaId;

            return (
              <li key={vinculo.sistema_id}>
                <Link
                  to={rotaDoVinculo(id, vinculo.sistema_id)}
                  state={estado}
                  aria-current={aberta}
                  className={[
                    "flex items-center gap-2 rounded-controle border bg-superficie-2 px-3 py-2",
                    aberta
                      ? "border-fio-forte shadow-[inset_2px_0_0_var(--esferografica)]"
                      : "border-fio hover:border-fio-forte",
                  ].join(" ")}
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-texto">{vinculo.sistema_nome}</span>
                    <span className="dados truncate text-[12px] text-texto-2">
                      {contratoDoVinculo(vinculo)} · {consumoDoPar(vinculo.consumo)}
                    </span>
                    {!motivoJaDito(prontidao, vinculo.sistema_id, vinculo.motivo_nao_emite) && (
                      <MotivoDoPar motivo={vinculo.motivo_nao_emite} />
                    )}
                  </span>
                  <SituacaoDoPar emite={vinculo.emite} motivo={vinculo.motivo_nao_emite} />
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {/*
        O papel é conferido antes de montar, porque a URL qualquer um cola. `data` também:
        o modal decide pelo número de vínculos como abre, e não pode decidir sem eles.
      */}
      {modal.abrir === "vincular" && ehAdmin && pessoa !== undefined && data !== undefined && (
        <VincularAplicacao
          pessoaID={id}
          pessoaNome={pessoa.razao_social}
          jaVinculadas={new Set(vinculos.map((vinculo) => vinculo.sistema_id))}
          aoFechar={modal.fechar}
        />
      )}

      <Outlet />
    </div>
  );
}
